import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faLoader } from "@fortawesome/pro-solid-svg-icons";

export default function ModalViewSignatures({ file, onClose }) {
    const [signatures, setSignatures] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (file) {
            loadSignatures();
        }
    }, [file]);

    const loadSignatures = async () => {
        setLoading(true);
        const sigs = [];

        console.log('Loading signatures for file:', {
            fileId: file.id,
            signature_id: file.signature_id,
            signature_joint_id: file.signature_joint_id,
            signature_landlord_id: file.signature_landlord_id
        });

        try {
            // Load main signature
            if (file.signature_id) {
                const mainResponse = await axios.post(
                    `${process.env.REACT_APP_API_URL}preview_file`,
                    { fileId: file.signature_id }
                );
                if (mainResponse.data) {
                    sigs.push({
                        id: file.signature_id,
                        type: "main",
                        label: "Main Signature",
                        name: mainResponse.data.name || "Main Signature",
                        url: mainResponse.data.url,
                        color: "blue"
                    });
                }
            }

            // Load joint signature
            if (file.signature_joint_id) {
                const jointResponse = await axios.post(
                    `${process.env.REACT_APP_API_URL}preview_file`,
                    { fileId: file.signature_joint_id }
                );
                if (jointResponse.data) {
                    sigs.push({
                        id: file.signature_joint_id,
                        type: "joint",
                        label: "Joint Signature",
                        name: jointResponse.data.name || "Joint Signature",
                        url: jointResponse.data.url,
                        color: "purple"
                    });
                }
            }

            // Load landlord signature
            if (file.signature_landlord_id) {
                const landlordResponse = await axios.post(
                    `${process.env.REACT_APP_API_URL}preview_file`,
                    { fileId: file.signature_landlord_id }
                );
                if (landlordResponse.data) {
                    sigs.push({
                        id: file.signature_landlord_id,
                        type: "landlord",
                        label: "Landlord Signature",
                        name: landlordResponse.data.name || "Landlord Signature",
                        url: landlordResponse.data.url,
                        color: "green"
                    });
                }
            }

            setSignatures(sigs);
        } catch (error) {
            console.error("Error loading signatures:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition.Root show={!!file} as={Fragment}>
            <Dialog as="div" className="relative z-[200]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="sm:flex sm:items-start">
                                    <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                                            Document Signatures
                                        </Dialog.Title>

                                        {loading ? (
                                            <div className="flex justify-center items-center py-12">
                                                <FontAwesomeIcon icon={faLoader} className="text-gray-400 text-3xl animate-spin" />
                                            </div>
                                        ) : signatures.length === 0 ? (
                                            <div className="text-center py-12 text-gray-500">
                                                No signatures found for this document
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 gap-6">
                                                {signatures.map((sig) => (
                                                    <div key={`${sig.type}-${sig.id}`} className="border rounded-lg p-6 bg-gray-50">
                                                        <div className={`text-sm font-semibold mb-1 text-${sig.color}-600`}>
                                                            {sig.label}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mb-2 truncate" title={sig.name}>
                                                            {sig.name}
                                                        </div>
                                                        <div className="text-xs text-gray-400 mb-4">
                                                            File ID: {sig.id}
                                                        </div>
                                                        <div className="bg-white border rounded p-8 flex items-center justify-center min-h-[150px]">
                                                            <img
                                                                src={sig.url}
                                                                alt={sig.label}
                                                                className="w-full h-auto max-w-2xl object-contain"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
